import {
	ChevronDownIcon,
	cn,
	type DeliveryMethod,
	deliveryMethodIcons,
} from "@au-schein-de/au-ui-library";
import { DELIVERY_METHOD_KINDS } from "@dransay/api/models";
import { MapPinIcon } from "lucide-react";
import {
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { useOutsideDismiss } from "~/hooks/use-outside-dismiss.hook";
import type { DeliveryMethodSelectorContentProps } from "./delivery-method-selector.shared";

export interface DeliveryMethodSelectorProps
	extends DeliveryMethodSelectorContentProps {}

/** Styled delivery method selector with built-in popover content. */
export function DeliveryMethodSelector({
	selectedMethod,
	onMethodSelect,
	deliveryMethodLabel,
	getMethodLabel,
	addressLabel,
	addressText,
	isAddressPlaceholder,
	addressActionLabel,
	onAddressAction,
	hiddenMethods,
	expressPromoSlot,
}: Readonly<DeliveryMethodSelectorProps>) {
	const panelId = useId();
	const deliveryMethodHeadingId = useId();
	const addressHeadingId = useId();
	const [open, setOpen] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const selectedDeliveryMethod = selectedMethod as DeliveryMethod;
	const visibleMethods = useMemo(
		() =>
			hiddenMethods?.length
				? DELIVERY_METHOD_KINDS.filter((m) => !hiddenMethods.includes(m))
				: DELIVERY_METHOD_KINDS,
		[hiddenMethods],
	);
	const closePopover = useCallback(() => {
		setOpen(false);
		requestAnimationFrame(() => triggerRef.current?.focus());
	}, []);

	const handleAddressAction = useCallback(() => {
		if (!onAddressAction) {
			return;
		}
		closePopover();
		void onAddressAction();
	}, [closePopover, onAddressAction]);

	useOutsideDismiss({ isOpen: open, wrapperRef, onDismiss: closePopover });

	useEffect(() => {
		if (!open) {
			return;
		}
		requestAnimationFrame(() => {
			const firstFocusable = panelRef.current?.querySelector<HTMLElement>(
				"input, button, [tabindex]:not([tabindex='-1'])",
			);
			firstFocusable?.focus();
		});
	}, [open]);

	return (
		<div ref={wrapperRef} className="relative w-[372px] min-w-[250px] shrink">
			{open &&
				createPortal(
					<div
						className="pointer-events-none fixed top-[88px] left-0 right-0 bottom-0 z-[19] bg-[rgba(16,17,15,0.5)]"
						data-testid="delivery-method-selector-overlay"
						aria-hidden="true"
					/>,
					document.body,
				)}
			<button
				ref={triggerRef}
				type="button"
				aria-label={`${deliveryMethodLabel}: ${getMethodLabel(selectedMethod)}, ${addressLabel}: ${addressText}`}
				aria-haspopup="dialog"
				data-testid="delivery-method-selector-trigger"
				aria-expanded={open}
				aria-controls={panelId}
				className="flex h-12 w-full max-w-[372px] min-w-0 items-center gap-2 rounded-xl bg-brand-600 pl-2 pr-6 py-2 cursor-pointer"
				onClick={() => setOpen(!open)}
			>
				<div className="flex size-8 shrink-0 items-center justify-center rounded-[6px] bg-ansay-900 text-ansay-light">
					<span aria-hidden="true" className="[&_svg]:pointer-events-none">
						{deliveryMethodIcons[selectedDeliveryMethod]}
					</span>
				</div>
				<div
					data-testid="delivery-method-selector-delivery-type"
					className="flex h-6 shrink-0 items-center gap-2 pr-2 text-body-sm-emphasis text-ansay-light"
				>
					<span>{getMethodLabel(selectedMethod)}</span>
					<span
						aria-hidden="true"
						className={cn(
							"transition-transform [&_svg]:pointer-events-none",
							open && "rotate-180",
						)}
					>
						<ChevronDownIcon size={5} />
					</span>
				</div>
				<span
					data-testid="delivery-method-selector-address"
					className={cn(
						"min-w-0 flex-1 truncate tracking-[-0.28px] text-right",
						isAddressPlaceholder
							? "text-body-sm-emphasis text-ansay-light"
							: "text-body-sm text-gray-100",
					)}
				>
					{addressText}
				</span>
			</button>
			{open && (
				<div
					ref={panelRef}
					id={panelId}
					role="dialog"
					aria-labelledby={`${deliveryMethodHeadingId} ${addressHeadingId}`}
					className="absolute left-0 top-full z-50 mt-2 w-[372px] flex flex-col gap-6 rounded-2xl bg-brand-600 p-6 shadow-[0px_10px_20px_0px_rgba(33,42,54,0.16)]"
					data-testid="delivery-method-selector-popover"
				>
					<fieldset className="flex flex-col gap-2">
						<legend className="sr-only">{deliveryMethodLabel}</legend>
						<div className="flex items-center">
							<span
								id={deliveryMethodHeadingId}
								className="text-body-emphasis text-gray-100"
							>
								{deliveryMethodLabel}
							</span>
						</div>
						<div className="flex gap-1 rounded-xl bg-white p-1">
							{visibleMethods.map((method) => (
								<label
									key={method}
									data-testid={`delivery-method-selector-option-label-${method}`}
									className={cn(
										"flex h-10 flex-1 cursor-pointer items-center justify-center gap-[5px] rounded-xl p-2 text-body-sm-emphasis transition-colors",
										method === selectedMethod
											? "bg-ansay-900 text-ansay-light"
											: "bg-gray-200 text-gray-700 hover:bg-gray-300",
									)}
								>
									<input
										type="radio"
										name="delivery-method-selector"
										className="sr-only"
										checked={method === selectedMethod}
										onChange={() => {
											void onMethodSelect(method, closePopover);
										}}
										data-testid={`delivery-method-selector-option-${method}`}
									/>
									<span
										aria-hidden="true"
										className="[&_svg]:pointer-events-none"
									>
										{deliveryMethodIcons[method as DeliveryMethod]}
									</span>
									<span>{getMethodLabel(method)}</span>
								</label>
							))}
						</div>
					</fieldset>
					{/* No wrapper div: a slot that renders null must add no box, or
					the flex gap above/below would still apply to an empty node. */}
					{expressPromoSlot}
					<div className="flex flex-col gap-2">
						<span
							id={addressHeadingId}
							className="text-body-emphasis text-gray-100"
						>
							{addressLabel}
						</span>
						<div className="flex h-12 items-center justify-between rounded-xl bg-gray-100 px-4">
							<button
								type="button"
								data-testid="delivery-method-selector-address-field"
								className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left disabled:cursor-default"
								onClick={handleAddressAction}
								disabled={!onAddressAction}
								aria-label={`${addressLabel}: ${addressText}`}
							>
								<MapPinIcon
									aria-hidden="true"
									className="size-6 shrink-0 text-gray-700"
								/>
								<span className="truncate text-body-sm tracking-[-0.28px] text-gray-700">
									{addressText}
								</span>
							</button>
							{onAddressAction && addressActionLabel && (
								<button
									type="button"
									data-testid="delivery-method-selector-address-action"
									className="shrink-0 text-body-emphasis text-ansay-700 cursor-pointer"
									onClick={handleAddressAction}
								>
									{addressActionLabel}
								</button>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
